import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  ButtonGroup,
  Chip,
  Alert,
  InputAdornment,
} from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import axios from 'axios';

export default function OrderEntry({ apiUrl, onOrderPlaced }) {
  const [orderType, setOrderType] = useState('BUY');
  const [priceType, setPriceType] = useState('MARKET');
  const [formData, setFormData] = useState({
    symbol: '',
    exchange: 'NSE',
    quantity: 1,
    price: '',
    stopLoss: '',
    takeProfit: '',
    tradeMode: 'paper',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handlePlaceOrder = async () => {
    if (!formData.symbol || !formData.quantity) {
      setError('Symbol and quantity are required');
      return;
    }

    if (priceType === 'LIMIT' && !formData.price) {
      setError('Price is required for limit orders');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      
      // For now, create a manual trade through the trade execution endpoint
      const orderData = {
        symbol: formData.symbol.toUpperCase(),
        exchange: formData.exchange,
        order_type: orderType,
        quantity: parseInt(formData.quantity),
        price: priceType === 'MARKET' ? 0 : parseFloat(formData.price),
        trade_mode: formData.tradeMode,
        stop_loss: formData.stopLoss ? parseFloat(formData.stopLoss) : null,
        take_profit: formData.takeProfit ? parseFloat(formData.takeProfit) : null,
      };

      // Call manual order API (we'll create this)
      const response = await axios.post(
        `${apiUrl}/orders/manual`,
        orderData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(`Order placed successfully! Order ID: ${response.data.order_id}`);
      setFormData({
        ...formData,
        quantity: 1,
        price: '',
        stopLoss: '',
        takeProfit: '',
      });
      
      if (onOrderPlaced) onOrderPlaced();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const isBuy = orderType === 'BUY' || orderType === 'SHORT';

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Place Order
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ButtonGroup fullWidth>
            <Button
              variant={orderType === 'BUY' ? 'contained' : 'outlined'}
              color="success"
              onClick={() => setOrderType('BUY')}
              startIcon={<TrendingUp />}
            >
              BUY
            </Button>
            <Button
              variant={orderType === 'SELL' ? 'contained' : 'outlined'}
              color="error"
              onClick={() => setOrderType('SELL')}
              startIcon={<TrendingDown />}
            >
              SELL
            </Button>
            <Button
              variant={orderType === 'SHORT' ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => setOrderType('SHORT')}
            >
              SHORT
            </Button>
            <Button
              variant={orderType === 'EXIT' ? 'contained' : 'outlined'}
              onClick={() => setOrderType('EXIT')}
            >
              EXIT
            </Button>
          </ButtonGroup>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Symbol"
            value={formData.symbol}
            onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
            placeholder="e.g., RELIANCE, NIFTY"
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Exchange</InputLabel>
            <Select
              value={formData.exchange}
              label="Exchange"
              onChange={(e) => setFormData({ ...formData, exchange: e.target.value })}
            >
              <MenuItem value="NSE">NSE (Equity)</MenuItem>
              <MenuItem value="NFO">NFO (Futures & Options)</MenuItem>
              <MenuItem value="BSE">BSE</MenuItem>
              <MenuItem value="MCX">MCX</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <ButtonGroup fullWidth size="small">
            <Button
              variant={priceType === 'MARKET' ? 'contained' : 'outlined'}
              onClick={() => setPriceType('MARKET')}
            >
              Market Order
            </Button>
            <Button
              variant={priceType === 'LIMIT' ? 'contained' : 'outlined'}
              onClick={() => setPriceType('LIMIT')}
            >
              Limit Order
            </Button>
          </ButtonGroup>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Quantity (Lots)"
            value={formData.quantity}
            onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
            required
          />
        </Grid>

        {priceType === 'LIMIT' && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Price"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
              required
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Stop Loss (Optional)"
            value={formData.stopLoss}
            onChange={(e) => setFormData({ ...formData, stopLoss: e.target.value })}
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Take Profit (Optional)"
            value={formData.takeProfit}
            onChange={(e) => setFormData({ ...formData, takeProfit: e.target.value })}
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Trade Mode</InputLabel>
            <Select
              value={formData.tradeMode}
              label="Trade Mode"
              onChange={(e) => setFormData({ ...formData, tradeMode: e.target.value })}
            >
              <MenuItem value="paper">
                <Chip label="Paper" size="small" sx={{ mr: 1 }} />
                Paper Trading (Simulated)
              </MenuItem>
              <MenuItem value="real">
                <Chip label="Real" color="primary" size="small" sx={{ mr: 1 }} />
                Real Trading (Live)
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            color={isBuy ? 'success' : 'error'}
            onClick={handlePlaceOrder}
            disabled={loading}
          >
            {loading ? 'Placing Order...' : `Place ${orderType} Order`}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
}